import { Button, EllipsisSpan, Table, useBreadcrumbRoutes, Wrapper, FormContent } from '@maxtropy/components';
import { useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import styles from './index.module.scss';
import { useRequest } from 'ahooks';
import { apiV2PipelinePressureMonitoringViewPipelinePressureMonitoringPost } from '@maxtropy/device-customer-apis-v2';
import dayjs from 'dayjs';
import { Space } from 'antd';
import { isNil } from 'lodash-es';

// 表格数据类型
interface TableDataRow {
  zoneName?: string;
  pressureGaugeNames?: string;
  startTime?: string;
  endTime?: string;
  duration?: number;
  physicalUnitName?: string;
  totalPressureDrop?: number;
  timePeriodDetails?: { time?: string; pressure?: number }[];
  timePeriodPressureDropDetails?: { startTime?: string; endTime?: string; pressureDrop?: number }[];
}

const PipelinePressureDetail: React.FC = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // 从路由 state 获取任务名称
  const taskName = (location.state as any)?.taskName || '压力监测任务';

  const { data: apiResponse, loading } = useRequest(
    async () => await apiV2PipelinePressureMonitoringViewPipelinePressureMonitoringPost({ id: Number(id) })
  );

  // 从 API 响应中提取表格数据
  const tableData: TableDataRow[] = useMemo(() => {
    const list = apiResponse?.list || [];
    return list.map(item => ({
      zoneName: item.areaInfo?.map(area => area.zoneName).join(', ') || '-',
      pressureGaugeNames: item.devices?.map(device => device.name).join(', ') || '-',
      startTime: dayjs(item.startTime).format('YYYY-MM-DD HH:mm') || '-',
      endTime: dayjs(item.endTime).format('YYYY-MM-DD HH:mm') || '-',
      duration: item.duration,
      physicalUnitName: item.physicalUnitName,
      totalPressureDrop: item.totalPressureDrop,
      timePeriodDetails: item.timePeriodDetails || [],
      timePeriodPressureDropDetails: item.timePeriodPressureDropDetails || [],
    }));
  }, [apiResponse]);

  // 格式化时长为 xxHxxmin 格式
  const formatDuration = (minutes?: number) => {
    if (minutes === undefined || minutes === null) return '-';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours > 0 && mins > 0) {
      return `${hours}H${mins}min`;
    } else if (hours > 0) {
      return `${hours}H`;
    } else {
      return `${mins}min`;
    }
  };

  // 从 timePeriodDetails 中动态生成时间段列
  const timePeriodColumns = useMemo(() => {
    if (!tableData || tableData.length === 0) return [];

    // 收集所有的时间点
    const allTimes = new Set<string>();
    tableData.forEach(row => {
      row.timePeriodDetails?.forEach(detail => {
        if (detail.time) allTimes.add(dayjs(detail.time).format('HH:mm'));
      });
    });
    const sortedTimes = Array.from(allTimes).sort();
    return sortedTimes.map((time, index) => {
      let columnTitle: string;
      if (index === 0) {
        columnTitle = `试验开始时间`;
      } else if (index === sortedTimes.length - 1) {
        columnTitle = `试验结束时间`;
      } else {
        columnTitle = `试验中间时间`;
      }
      return {
        title: (
          <>
            {columnTitle}
            <br />
            压力-{time}
          </>
        ),
        key: `pressure_${time}`,
        dataIndex: 'timePeriodDetails',
        width: 130,
        render: (_: unknown, record: TableDataRow) => {
          const physicalUnitName = record.physicalUnitName ?? '-';
          const detail = record.timePeriodDetails?.find(d => dayjs(d.time).format('HH:mm') === time);
          return <EllipsisSpan value={!isNil(detail?.pressure) ? `${detail.pressure}${physicalUnitName}` : '-'} />;
        },
      };
    });
  }, [tableData]);

  // 从 timePeriodPressureDropDetails 中动态生成压力下降幅度列
  const pressureDropColumns = useMemo(() => {
    if (!tableData || tableData.length === 0) return [];
    const dropColumns: any[] = [];
    tableData.forEach(row => {
      row.timePeriodPressureDropDetails?.forEach(detail => {
        const key = `${detail.startTime}_${detail.endTime}`;
        if (!dropColumns.find(col => col.key === key)) {
          dropColumns.push({
            title: `压力下降幅度 (${dayjs(detail.startTime).format('HH:mm')}~${dayjs(detail.endTime).format('HH:mm')})`,
            key,
            dataIndex: 'timePeriodPressureDropDetails',
            width: 160,
            render: (_: unknown, record: TableDataRow) => {
              const detailFound = record.timePeriodPressureDropDetails?.find(
                d => d.startTime === detail.startTime && d.endTime === detail.endTime
              );
              return <EllipsisSpan value={!isNil(detailFound?.pressureDrop) ? `${detailFound.pressureDrop}%` : '-'} />;
            },
          });
        }
      });
    });
    // 添加最后一列：压力总体下降幅度
    dropColumns.push({
      title: `压力总体下降幅度 ${dayjs(tableData[0].startTime).format('HH:mm')}~${dayjs(tableData[0].endTime).format(
        'HH:mm'
      )}`,
      dataIndex: 'totalPressureDrop',
      width: 160,
      render: (v: number) => <EllipsisSpan value={!isNil(v) ? `${v}%` : '-'} />,
    });
    return dropColumns;
  }, [tableData]);

  // 表格列配置
  const columns = useMemo(
    () => [
      {
        title: '站房/区域',
        dataIndex: 'zoneName',
        ellipsis: true,
        width: 150,
        render: (v: string) => <EllipsisSpan value={v} />,
      },
      {
        title: '压力表',
        dataIndex: 'pressureGaugeNames',
        ellipsis: true,
        width: 150,
        render: (v: string) => <EllipsisSpan value={v} />,
      },
      {
        title: '试验开始时间',
        dataIndex: 'startTime',
        ellipsis: true,
        width: 160,
        render: (v: string) => <EllipsisSpan value={v} />,
      },
      ...timePeriodColumns,
      {
        title: '试验结束时间',
        dataIndex: 'endTime',
        ellipsis: true,
        width: 160,
        render: (v: string) => <EllipsisSpan value={v} />,
      },
      {
        title: '试验时长',
        dataIndex: 'duration',
        ellipsis: true,
        width: 100,
        render: (v: number) => <EllipsisSpan value={formatDuration(v)} />,
      },
      ...pressureDropColumns,
    ],
    [timePeriodColumns, pressureDropColumns]
  );

  // 处理下载
  const handleDownload = () => {
    window.open(`/api/v2/pipelinePressureMonitoring/export?id=${id}`);
  };

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes || []), { name: '查看' }]} className={styles.wrapper}>
      <FormContent narrow title={taskName}>
        <div className={styles.headerActions}>
          <Button type="primary" onClick={handleDownload}>
            下载
          </Button>
        </div>
        <Table
          sticky
          columns={columns}
          dataSource={tableData}
          scroll={{ x: 2400 }}
          loading={loading}
          pagination={false}
        />
        <Space className="sticky-footer">
          <Button onClick={() => navigate('/pipeline/pressure/monitoring')}>返回</Button>
        </Space>
      </FormContent>
    </Wrapper>
  );
};

export default PipelinePressureDetail;
